import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Brain, Play } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';

export default function Library() {
  const { user } = useAuth();
  const isDemo = user?.role === 'PARTNER_DEMO';
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/library').then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);

  if (items === null) {
    return <div className="max-w-6xl mx-auto px-4 py-16"><div className="h-64 rounded-2xl bg-slate-100 animate-pulse" /></div>;
  }

  return (
    <main className="page-shell py-10 sm:py-14" data-testid="library-page">
      {isDemo && <div className="mb-8 rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-4" role="status" data-testid="partner-demo-banner">
        <p className="text-xs font-bold tracking-[0.14em] text-brand">CORSO DIMOSTRATIVO PARTNER</p>
        <p className="mt-1 text-sm text-slate-600">Questa area serve esclusivamente a mostrare il funzionamento di ConcorsiEssentials e non costituisce accesso a un corso commerciale completo.</p>
      </div>}
      <p className="eyebrow mb-3">Area personale</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.04em] font-bold text-brand-dark mb-2" data-testid="library-title">
        Da dove riprendi?
      </h1>
      <p className="text-slate-600 text-lg mb-10">Il prossimo passo del tuo percorso, senza doverlo cercare.</p>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-14 text-center" data-testid="library-empty">
          <BookOpen className="mx-auto text-slate-300 mb-4" size={40} strokeWidth={1.5} />
          <p className="text-slate-600 font-medium mb-2">Nessun concorso acquistato</p>
          <p className="text-sm text-slate-500 mb-6">Esplora il catalogo e inizia a prepararti.</p>
          <Button onClick={() => navigate('/concorsi')} data-testid="library-browse"
            className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            Sfoglia il catalogo
          </Button>
        </div>
      ) : (
        <div className="grid gap-6">
          {items.map(({ purchase, contest, stats, last_section_title }) => (
            <div key={purchase.purchase_id} data-testid={`library-item-${contest.contest_id}`}
              className="surface overflow-hidden p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-7 relative">
              <div>
                <p className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold mb-1">{contest.ente}</p>
                <Link to={`/reader/${contest.contest_id}`} className="font-display text-lg sm:text-xl font-semibold text-slate-900 hover:text-brand transition-colors">
                  {contest.title}
                </Link>
                {purchase.access_type === 'BOOK_INCLUDED' && <p className="mt-2 text-sm text-brand">Accesso incluso nel libro · {purchase.expires_at ? `valido fino al ${new Date(purchase.expires_at).toLocaleDateString('it-IT')}` : 'senza scadenza'}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2 mb-5">
                  {(contest.subjects || []).map((s) => (
                    <span key={s} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">{s}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Completato</p>
                    <p className="font-display font-bold text-2xl text-brand" data-testid={`progress-${contest.contest_id}`}>{stats.percent}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Sezioni</p>
                    <p className="font-display font-bold text-lg text-slate-900">{stats.completed_count}/{stats.total_sections}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Quiz fatti</p>
                    <p className="font-display font-bold text-lg text-slate-900">{stats.quiz_attempts}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Media quiz</p>
                    <p className="font-display font-bold text-lg text-slate-900">{stats.quiz_avg !== null ? `${stats.quiz_avg}%` : '—'}</p>
                  </div>
                </div>
                <Progress value={stats.percent} className="h-2.5 mb-3" aria-label={`Preparazione ${stats.percent}%`} />
                {last_section_title && (
                  <p className="text-xs text-slate-500">Ultimo punto studiato: <span className="font-medium text-slate-700">{last_section_title}</span></p>
                )}
              </div>
              <div className="flex md:flex-col gap-3 md:justify-center md:w-44">
                <Button onClick={() => navigate(`/reader/${contest.contest_id}`)} data-testid={`resume-${contest.contest_id}`}
                  className="flex-1 min-h-12 rounded-full bg-brand hover:bg-brand-hover text-white">
                  <Play size={14} strokeWidth={1.5} className="mr-1.5" /> Continua a studiare
                </Button>
                <Button variant="outline" onClick={() => navigate(`/quiz/${contest.contest_id}`)} data-testid={`quiz-${contest.contest_id}`}
                  className="flex-1 min-h-12 rounded-full">
                  <Brain size={14} strokeWidth={1.5} className="mr-1.5" /> Quiz
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
